import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { LayoutGrid, FolderKanban, TrendingUp, Sparkles, Settings, LogOut, Wrench, Bell, Target, CalendarCheck, Palette, Search, FileText, BarChart3, Users, AlertTriangle, Menu, X } from "lucide-react";

const NAV = [
  { to: "/app/overview", label: "Executive summary", icon: LayoutGrid },
  { to: "/app/projects", label: "Projects", icon: FolderKanban },
  // Temporarily hidden — "What's changed" is being redesigned. Reinstate this
  // entry once the new page ships.
  // { to: "/app/changes", label: "What's changed", icon: TrendingUp },
  { to: "/app/opportunity", label: "Opportunity", icon: Target },
  { to: "/app/alerts", label: "Alerts", icon: Bell, badge: true },
  { to: "/app/reports", label: "Reports", icon: CalendarCheck },
  { to: "/app/team", label: "Team seats", icon: Users },
  { to: "/app/branding", label: "White-label", icon: Palette },
  { to: "/app/seo", label: "SEO settings", icon: Search, adminOnly: true },
  { to: "/app/cms", label: "Blog posts", icon: FileText, adminOnly: true },
  { to: "/app/admin", label: "Platform admin", icon: BarChart3, adminOnly: true },
  // Temporarily hidden — AI analysis is being redesigned. Reinstate this
  // entry once the new page ships.
  // { to: "/app/ai", label: "AI analysis", icon: Sparkles },
  { to: "/app/tools", label: "Free tools", icon: Wrench },
  { to: "/app/settings", label: "Billing", icon: Settings },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const loadUnread = () => api.get("/alerts/count").then((r) => setUnread(r.data.unread || 0)).catch(() => {});

  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 60_000);
    return () => clearInterval(t);
  }, []);

  // Auto-close mobile drawer on route change so the user actually lands
  // on the new page instead of staring at the still-open menu.
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Mobile top bar — hamburger + logo. Hidden on md+ where the persistent
          sidebar owns navigation. Sticky so it stays reachable while scrolling. */}
      <header className="md:hidden sticky top-0 z-30 bg-white border-b border-[#E2E8F0] h-14 px-4 flex items-center justify-between">
        <button
          data-testid="mobile-nav-toggle"
          onClick={() => setMobileOpen(true)}
          aria-label="Open navigation"
          className="p-2 -ml-2 rounded-md text-[#0A0D14] hover:bg-[#F8F9FA]"
        >
          <Menu className="h-5 w-5" />
        </button>
        <Logo />
        <div className="w-9" />
      </header>

      <div className="flex">
        {/* Mobile-drawer backdrop */}
        {mobileOpen && (
          <button
            aria-label="Close navigation"
            data-testid="mobile-nav-backdrop"
            onClick={() => setMobileOpen(false)}
            className="fixed inset-0 z-40 bg-black/40 md:hidden"
          />
        )}

        <aside
          data-testid="sidebar"
          className={`sn-sidebar fixed md:sticky top-0 left-0 z-50 h-screen md:h-screen shrink-0 flex flex-col transform transition-transform duration-200 ease-out ${
            mobileOpen ? "translate-x-0" : "-translate-x-full"
          } md:translate-x-0`}
        >
          <div className="h-16 flex items-center justify-between px-5 border-b border-[#E2E8F0] bg-white">
            <Logo />
            <button
              data-testid="mobile-nav-close"
              onClick={() => setMobileOpen(false)}
              aria-label="Close navigation"
              className="md:hidden p-1.5 -mr-1.5 rounded text-[#525B6A] hover:bg-[#F8F9FA]"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="p-3 space-y-1 flex-1 overflow-y-auto">
            {NAV.filter((n) => !n.adminOnly || user?.role === "admin").map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`sidebar-${n.label.toLowerCase().replace(/[^a-z]+/g,'-')}`}
                className={({ isActive }) => `sn-navlink ${isActive ? 'active' : ''} justify-between`}>
                <span className="inline-flex items-center gap-2.5">
                  <n.icon className="h-4 w-4" />
                  <span>{n.label}</span>
                </span>
                {n.badge && unread > 0 && (
                  <span data-testid="alerts-badge" className="tabular-nums text-[10px] font-semibold bg-[#0047FF] text-white rounded-full min-w-[18px] h-[18px] inline-flex items-center justify-center px-1">{unread > 99 ? '99+' : unread}</span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="p-4 border-t border-[#E2E8F0] bg-white">
            <div className="text-xs text-[#8A94A6] mb-1">Signed in as</div>
            <div className="text-sm font-medium text-[#0A0D14] truncate" data-testid="sidebar-user-email">{user?.email}</div>
            <div className="mt-2 flex items-center justify-between">
              <span className="pill pill-accent capitalize">{user?.plan || 'trial'}</span>
              <button onClick={() => { logout(); nav('/'); }} data-testid="sidebar-logout" className="text-xs text-[#525B6A] inline-flex items-center gap-1 hover:text-[#DC2626]">
                <LogOut className="h-3.5 w-3.5" /> Log out
              </button>
            </div>
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          {user?.trial_expired && (
            <div className="bg-amber-50 border-b border-amber-200 px-4 sm:px-6 py-3 flex items-center gap-3" data-testid="trial-expired-banner">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0" />
              <div className="flex-1 text-sm text-[#0A0D14]">
                <span className="font-semibold">Your 7-day free trial has ended.</span>
                <span className="text-[#525B6A]"> You can still view your projects and history, but adding new keywords or refreshing ranks is paused until you subscribe.</span>
              </div>
              <Link to="/app/settings" data-testid="trial-expired-cta" className="btn-primary px-4 py-1.5 rounded-md text-xs font-semibold shrink-0">Upgrade now</Link>
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
